import React from 'react';
import { DollarSign, Check, CreditCard, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';

export const RefundDialog = ({
  open, onOpenChange, creditMemo, paymentInfo, loadingPaymentInfo, manualRefundMethods,
  selectedRefundMethod, setSelectedRefundMethod, refundReference, setRefundReference,
  refundNotes, setRefundNotes, processingRefund, onConfirm,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md" data-testid="refund-dialog">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <DollarSign className="h-5 w-5 text-green-600" />
          Process Refund
        </DialogTitle>
        <DialogDescription>Choose how to process this refund</DialogDescription>
      </DialogHeader>

      {loadingPaymentInfo ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
        </div>
      ) : (
        <div className="space-y-4 pt-2">
          <div className="p-3 bg-gray-50 rounded-lg text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-gray-500">Credit Memo</span>
              <span className="font-medium">{creditMemo?.credit_memo_number}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Refund Amount</span>
              <span className="font-bold text-green-600">${creditMemo?.refund_amount?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Original Payment</span>
              <span className="font-medium">{paymentInfo?.payment_method || 'Unknown'}</span>
            </div>
          </div>

          {paymentInfo?.stripe_refund_eligible && (
            <div
              className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${selectedRefundMethod === 'stripe' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'}`}
              onClick={() => setSelectedRefundMethod('stripe')}
              data-testid="stripe-refund-option"
            >
              <div className="flex items-center gap-3">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedRefundMethod === 'stripe' ? 'border-green-500 bg-green-500' : 'border-gray-300'}`}>
                  {selectedRefundMethod === 'stripe' && <Check className="h-3 w-3 text-white" />}
                </div>
                <div className="flex-1">
                  <p className="font-medium">Stripe Refund</p>
                  <p className="text-sm text-gray-500">Refund to original payment method</p>
                </div>
                <CreditCard className="h-5 w-5 text-gray-400" />
              </div>
            </div>
          )}

          {paymentInfo?.stripe_refund_eligible === false && paymentInfo?.has_stripe_payment && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm">
              <p className="text-yellow-800">
                <AlertTriangle className="h-4 w-4 inline mr-1" />
                Stripe refund not available. {paymentInfo?.already_refunded_via_stripe ? 'Already refunded via Stripe.' : 'Use manual refund.'}
              </p>
            </div>
          )}

          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">Manual Refund Options</p>
            <div className="grid grid-cols-2 gap-2">
              {manualRefundMethods.map(method => (
                <div
                  key={method.value}
                  className={`p-3 border-2 rounded-lg cursor-pointer transition-all text-center ${selectedRefundMethod === method.value ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'}`}
                  onClick={() => setSelectedRefundMethod(method.value)}
                  data-testid={`refund-method-${method.value}`}
                >
                  <p className="text-sm font-medium">{method.label}</p>
                </div>
              ))}
            </div>
          </div>

          {selectedRefundMethod && selectedRefundMethod !== 'stripe' && (
            <div className="space-y-3">
              <div>
                <Label htmlFor="refund-reference">Reference # (optional)</Label>
                <Input
                  id="refund-reference"
                  placeholder="Check #, Transaction ID..."
                  value={refundReference}
                  onChange={(e) => setRefundReference(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="refund-notes">Notes (optional)</Label>
                <Textarea
                  id="refund-notes"
                  placeholder="Additional notes..."
                  rows={2}
                  value={refundNotes}
                  onChange={(e) => setRefundNotes(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              className="flex-1 bg-green-600 hover:bg-green-700"
              onClick={onConfirm}
              disabled={processingRefund || !selectedRefundMethod}
              data-testid="confirm-refund-btn"
            >
              {processingRefund ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <DollarSign className="h-4 w-4 mr-2" />}
              {processingRefund ? 'Processing...' : 'Process Refund'}
            </Button>
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);
